import { describeAdjustments, type Nudge, type PieceAdjustment, type PieceKind } from '@studio/shared';

/**
 * A bench draft: what the operator moved, held locally and nowhere else.
 *
 * Nothing in here has reached the Studio. That is the whole point - an accidental nudge
 * must not change history - so the draft is ordinary local state until `Create edited
 * Candidate` sends it, and discarding it is simply forgetting it.
 */
export interface Draft {
  readonly adjustments: readonly PieceAdjustment[];
  readonly instruction: string;
}

export const EMPTY_DRAFT: Draft = { adjustments: [], instruction: '' };

/** One relative move of one piece, appended in the order it was made. */
export function nudge(draft: Draft, piece: PieceKind, move: Nudge): Draft {
  const standing = draft.adjustments.find((adjustment) => adjustment.piece === piece);
  const adjustments =
    standing === undefined
      ? [...draft.adjustments, { piece, nudges: [move] }]
      : draft.adjustments.map((adjustment) =>
          adjustment.piece === piece ? { piece, nudges: [...adjustment.nudges, move] } : adjustment,
        );
  return { ...draft, adjustments };
}

export function withInstruction(draft: Draft, instruction: string): Draft {
  return { ...draft, instruction };
}

/** Whether there is anything to make a descendant from yet. */
export function isDirty(draft: Draft): boolean {
  return draft.adjustments.some((adjustment) => adjustment.nudges.length > 0) || draft.instruction.trim() !== '';
}

/**
 * The draft read back to the operator, in the same words the descendant will be made
 * from. The sentence comes from the shared contract, so the bench cannot describe one
 * change while the Studio generates another.
 */
export function describeDraft(draft: Draft): string {
  const moved = describeAdjustments(draft.adjustments);
  const instruction = draft.instruction.trim();
  return [moved, instruction].filter((part) => part !== '').join('. ');
}
